import { Attribute, ProductBarcode, ProductBarcodeType } from '@/types/entity/product';

// EWM-936 — barcode type → i18n label key. A type absent from this map produces
// NO row, so an unrecognised source type can never leak a raw code into the UI
// (AC: "a type with no mapping is handled without exposing a raw code").
export const BARCODE_LABEL_KEY: Record<ProductBarcodeType, string> = {
  ean: 'pdp.barcode-ean',
  upc: 'pdp.barcode-upc',
  additional: 'pdp.barcode-additional',
};

/**
 * Turn a product's barcodes into specifications-table rows.
 *
 * One row per barcode TYPE, listing every value of that type. A product can
 * carry 9+ `additional_barcodes`, and nine consecutive rows all captioned
 * "Additional barcodes" would bloat the table without telling the customer
 * anything more — so the values share a row and are joined with ", ", which
 * also gives long GS1 case codes somewhere to wrap at mobile widths.
 *
 * Values are emitted verbatim: the AC requires they display exactly as held in
 * the source, and leading zeros / length are significant on GS1 codes.
 *
 * `translate` is injected rather than hooked so this stays pure and testable.
 */
export const barcodeSpecificationRows = (
  barcodes: ProductBarcode[] | undefined,
  translate: (key: string) => string,
): Attribute[] => {
  if (!barcodes || barcodes.length === 0) return [];

  const valuesByType = new Map<ProductBarcodeType, string[]>();
  for (const { type, value } of barcodes) {
    if (!(type in BARCODE_LABEL_KEY)) continue;
    const bucket = valuesByType.get(type);
    if (bucket) bucket.push(value);
    else valuesByType.set(type, [value]);
  }

  // Insertion order is the mapper's emit order: EAN, UPC, then additional.
  return Array.from(valuesByType, ([type, values]) => ({
    label: translate(BARCODE_LABEL_KEY[type]),
    value: values.join(', '),
  }));
};
